import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useStateStore } from './state.js'

export const useResultsStore = defineStore('results', () => {
   const apiUrl = process.env.VUE_APP_API_URL
  const results = ref([])
  const markers = ref([])
  const markersMap = ref({})
  const splits = ref([])
  const splitsMap = ref([])
  const event = ref(null)
  const categories = ref([])
  const leader = ref(null)

  function fetchResults(eventAlias) {
    fetch(`${apiUrl}/api/v1/splits/${eventAlias}`)
      .then(response => response.json())
      .then(data => {
        // Store event and categories data from splits response
        event.value = data.event || null
        categories.value = data.categories || []

        markers.value = data.markers.sort((a, b) => {
          return a.sort - b.sort
        }) || []
        splits.value = data.splits || []
        markersMap.value = getMarkersMap()
        markersMap.value = Object.fromEntries(
          Object.entries(markersMap.value)
            .sort(([, a], [, b]) => a.sort - b.sort)
        )
        splitsMap.value = getSplitsMap()
        
        data.runners.forEach(runner => {
          const startTime = getStartTime(runner)
          
          // Calculate elapsed time for time-based calculations
          if (runner.finish_time) {
            const finishTime = new Date(runner.finish_time)
            if (startTime) {
              let elapsedTime = finishTime - startTime
              // If elapsed time is negative, set to 0
              if (elapsedTime < 0) {
                elapsedTime = 0
              }
              runner.elapsedTime = elapsedTime
              // Hide zero times in display
              runner.timeScore = elapsedTime === 0 ? '' : getHHMMSSString(new Date(elapsedTime))
            }
          } else {
            let gpsFinishTime = getGpsFinishTime(runner, data.markers, data.splits)
            if (gpsFinishTime) {
              let elapsedTime = gpsFinishTime - startTime
              // If elapsed time is negative, set to 0
              if (elapsedTime < 0) {
                elapsedTime = 0
              }
              runner.elapsedTime = elapsedTime
              // Hide zero times in display
              runner.timeScore = elapsedTime === 0 ? '' : getHHMMSSString(new Date(elapsedTime))
            } else {
              runner.timeScore = '--:--:--'
            }
          }
          
          // Handle point-based scores (if runner.score is already a number from API)
          // Keep the original score if it's numeric, otherwise use time-based score
          if (typeof runner.score === 'number' && runner.score >= 0) {
            runner.pointScore = runner.score
          } else {
            runner.pointScore = null
            // Set score to time-based score for backward compatibility
            runner.score = runner.timeScore
          }
          runner.splits = {}
          splits.value.forEach(split => {
            if (split.runner_id == runner.id) {
              runner.splits[split.marker_id] = split
            }
          })
          // calculate last marker by sort and find the last split
          markers.value.forEach(marker => {
            let split = runner.splits[marker.id]
            if (split) {
              if (!runner.lastMarker || marker.sort > runner.lastMarker.sort) {
                runner.lastMarker = marker
                runner.lastSplit = split
              }
            }
          })

        })
        
        // Check if any runner has point-based scores to determine sorting method
        const hasPointScores = data.runners.some(runner => runner.pointScore !== null && runner.pointScore > 0)
        
        data.runners.sort((a, b) => {
          if (a.abs) {
            return 1
          }
          if (b.abs) {
            return -1
          }
          if (a.dnf) {
            return 1
          }
          if (b.dnf) {
            return -1
          }
          
          // If we have point-based scores, sort by points first (higher points = better position)
          if (hasPointScores) {
            const aPoints = a.pointScore || 0
            const bPoints = b.pointScore || 0
            
            // If both have points or both don't have points, compare them
            if ((aPoints > 0) === (bPoints > 0)) {
              if (aPoints > 0) {
                // Both have points - higher points wins (sort descending)
                if (aPoints !== bPoints) {
                  return bPoints - aPoints
                }
                // If points are equal, sort by time
                if (a.elapsedTime && b.elapsedTime) {
                  return a.elapsedTime - b.elapsedTime
                }
              } else {
                // Both have no points - sort by time
                if (a.elapsedTime && b.elapsedTime) {
                  return a.elapsedTime - b.elapsedTime
                }
              }
            } else {
              // One has points, one doesn't - the one with points wins
              return bPoints > 0 ? 1 : -1
            }
          } else {
            // No point-based scores - use original time-based sorting
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          }
          
          if (a.finish_time || a.finish_time_gps) {
            return -1
          }
          if (b.finish_time || b.finish_time_gps) {
            return 1
          }
          return a.number - b.number
        })
        results.value = data.runners

        // Calculate leader (fastest finisher who is not DNS/DNF)
        calculateLeader()

        // Calculate category positions
        calculateCategoryPositions()
      })
      .catch(error => {
        console.error('Error fetching results:', error)
      })
  }

  function clearResults() {
    results.value = []
    leader.value = null
  }

  
  function getStartTime(runner) {
    if (runner.start_time) {
      return new Date(runner.start_time)
    }
    if (useStateStore().currentEvent) {
      return useStateStore().currentEvent.date_start
    }
    return null
  }

  function getHHMMSSString(date) {
    // Handle negative or zero elapsed time - don't display
    const timestamp = date instanceof Date ? date.getTime() : (typeof date === 'number' ? date : 0)
    if (timestamp <= 0) {
      return ''
    }
    const hours = Math.floor(timestamp / (1000 * 60 * 60))
    const dateObj = date instanceof Date ? date : new Date(timestamp)
    const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
    const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
    return `${hours}:${minutes}:${seconds}`
  }

  function getGpsFinishTime(runner, markers, splits) {
    const finishMarker = markers.find(marker => marker.type == 8 || marker.type == 93)
    if (finishMarker) {
      const split = splits.find(split => split.marker_id == finishMarker.id && split.runner_id == runner.id)
      if (split) {
        runner.finish_time_gps = split.time
        return new Date(split.time)
      }
    }
    return null
  }

  function getMarkersMap() {
    return markers.value.reduce((map, marker) => {
      map[marker.id] = marker
      return map
    }, {})
  }

  function getSplitsMap() {
    return splits.value.reduce((map, split) => {
      if (!map[split.marker_id]) {
        map[split.marker_id] = []
      }
      map[split.marker_id].push(split)
      return map
    }, {})
  }

  function shouldShowCategoryColumn() {
    return results.value.some(runner => 
      runner.category_id != null && runner.category_id != 10
    )
  }

  function shouldShowPointsColumn() {
    return results.value.some(runner => 
      runner.pointScore !== null && runner.pointScore > 0
    )
  }

  function getCategoryName(categoryId) {
    if (!categoryId || !categories.value) return ''
    const category = categories.value.find(cat => cat.id == categoryId)
    return category ? category.name : ''
  }

  function getCategoryPosition(runner) {
    if (!runner.category_id || runner.category_id == 10) {
      return ''
    }

    const categoryName = getCategoryName(runner.category_id)
    if (!categoryName) return ''

    // If runner hasn't finished, show only category name
    if (!runner.finish_time && !runner.finish_time_gps) {
      return categoryName
    }

    // Get all runners in the same category
    const categoryRunners = results.value.filter(r => r.category_id == runner.category_id)
    
    // Check if any runner in this category has point scores
    const hasPointScores = categoryRunners.some(r => r.pointScore !== null && r.pointScore > 0)
    
    // Sort them using the same logic as the main results
    categoryRunners.sort((a, b) => {
      if (a.abs) {
        return 1
      }
      if (b.abs) {
        return -1
      }
      if (a.dnf) {
        return 1
      }
      if (b.dnf) {
        return -1
      }
      
      // If we have point-based scores, sort by points first
      if (hasPointScores) {
        const aPoints = a.pointScore || 0
        const bPoints = b.pointScore || 0
        
        if ((aPoints > 0) === (bPoints > 0)) {
          if (aPoints > 0) {
            if (aPoints !== bPoints) {
              return bPoints - aPoints
            }
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          } else {
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
          }
        } else {
          return bPoints > 0 ? 1 : -1
        }
      } else {
        if (a.elapsedTime && b.elapsedTime) {
          return a.elapsedTime - b.elapsedTime
        }
      }
      
      if (a.finish_time || a.finish_time_gps) {
        return -1
      }
      if (b.finish_time || b.finish_time_gps) {
        return 1
      }
      return a.number - b.number
    })

    // Find the position of this runner in the category
    const position = categoryRunners.findIndex(r => r.id === runner.id) + 1
    return `${categoryName} ${position}`
  }

  function calculateCategoryPositions() {
    // Pre-calculate category positions for all runners
    results.value.forEach(runner => {
      runner.categoryPosition = getCategoryPosition(runner)
    })
  }

  function calculateLeader() {
    // Check if we have point-based scoring
    const hasPointScores = results.value.some(r => r.pointScore !== null && r.pointScore > 0)
    
    if (hasPointScores) {
      // For point-based scoring, leader is the one with highest points (and fastest time as tiebreaker)
      leader.value = results.value.find(r => 
        !r.abs && !r.dnf && r.category_id !== 10 && r.pointScore > 0
      ) || null
    } else {
      // For time-based scoring, leader is the fastest finisher
      leader.value = results.value.find(r => 
        !r.abs && !r.dnf && r.elapsedTime != null && r.category_id !== 10
      ) || null
    }
  }

  function getGapToLeader(runner) {
    // If no leader found or this runner hasn't finished
    if (!leader.value || runner.abs || runner.dnf) {
      return null
    }
    
    // If this is the leader
    if (runner.id === leader.value.id) {
      return { timeGap: 0, pointGap: 0 }
    }
    
    const hasPoints = shouldShowPointsColumn()
    
    let timeGap = null
    let pointGap = null
    
    // Calculate time gap
    if (runner.elapsedTime && leader.value.elapsedTime) {
      timeGap = runner.elapsedTime - leader.value.elapsedTime
    }
    
    // Calculate point gap if using point-based scoring
    if (hasPoints) {
      const runnerPoints = runner.pointScore || 0
      const leaderPoints = leader.value.pointScore || 0
      pointGap = leaderPoints - runnerPoints // Leader has more points, so positive gap means runner is behind
    }
    
    return { timeGap, pointGap }
  }


  return { 
    results, 
    markersMap, 
    splitsMap, 
    event, 
    categories, 
    fetchResults, 
    clearResults, 
    shouldShowCategoryColumn, 
    shouldShowPointsColumn,
    getCategoryPosition,
    getGapToLeader
  }
})